import { Box, Checkbox, styled, useTheme, Typography } from '@mui/material';
import { TSocialMedia } from 'src/models/general';
import ButtonCircle from '../ButtonCircle';
import GetSocialMediaIcon from '../GetSocialMediaIcon';
import { themeColors } from 'src/theme/schemes/PureLightTheme';
import { useAppSelector } from 'src/app/hooks';

const Container = styled(Box)(
  () => `
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 6px 9px 6px 9px;
    border-radius:18px;
    background: #FBFBFB;
    border: 1px solid #E8E9EA;
    box-sizing: border-box;
  `
);

const CustomCheckbox = styled(Checkbox)<{ activeBgColor: string }>(
  ({ theme, activeBgColor }) => `
    padding: ${theme.spacing(1)} 0px ${theme.spacing(1)} 2px;
    width: ${theme.spacing(5)};
    height: ${theme.spacing(3)};
    border-radius: ${theme.spacing(1.43)};
    padding: 0;
    margin: 0;

    &.Mui-checked {
      background-color: ${activeBgColor};
      color: white;
    }

    &:not(.Mui-checked) {
      background-color: ${theme.palette.grey[100]};
      border: 1px solid ${theme.palette.grey[300]};
      color: ${theme.palette.grey[600]};
    }

    &:hover {
      background-color: ${activeBgColor};
      opacity: 0.8;
    }

    &:not(.Mui-checked):hover {
      background-color: ${theme.palette.grey[200]};
      opacity: 1;
    }

    .MuiSvgIcon-root {
      font-size: 16px;
    }
  `
);

interface Props {
  selectedPlatforms: TSocialMedia[];
  handleChange: (platform: TSocialMedia) => void;
  handleClickAll: () => void;
}

export const SOCIAL_MEDIA_LIST: TSocialMedia[] = [
  'youtube',
  'twitter',
  'facebook',
  'instagram',
  'tiktok',
  'linkedin',
  // 'googleReview',
  // 'telegram',
  // 'whatsapp',
  'threads',
  'reddit'
];

const FilterObjectByPlatform = ({
  selectedPlatforms,
  handleChange,
  handleClickAll
}: Props) => {
  const theme = useTheme();
  const isSelected = (platform: TSocialMedia) =>
    selectedPlatforms.includes(platform);

  const project = useAppSelector((state) => state.users.project);

  const getSocialMediaBgColor = (platform: TSocialMedia) => {
    switch (platform) {
      case 'youtube':
        return '#FF0000';
      case 'twitter':
        return '#000000';
      case 'facebook':
        return '#3B5998';
      case 'instagram':
        return '#7A3EAE';
      case 'tiktok':
        return '#051C26';
      case 'linkedin':
        return '#0077B5';
      case 'telegram':
        return '#0088CC';
      case 'whatsapp':
        return '#25D366';
      case 'threads':
        return themeColors.threads;
      case 'reddit':
        return themeColors.reddit;
      default:
        return '#E0F6FF';
    }
  };

  const showHiddenSocmeds =
    project.timezone === 8 ||
    project.id === 'c960b616-7847-472f-9e16-e47941536fd7';

  return (
    <Container>
      <ButtonCircle
        onClick={handleClickAll}
        sx={{
          marginRight: theme.spacing(2),
          background: '#E0F6FF'
        }}
      >
        <Typography
          sx={{ fontSize: '14px', color: '#009FE3', fontWeight: 600 }}
        >
          All
        </Typography>
      </ButtonCircle>
      {SOCIAL_MEDIA_LIST.map(
        (platform) =>
          (platform !== 'reddit' || showHiddenSocmeds) && (
            <CustomCheckbox
              key={platform}
              activeBgColor={getSocialMediaBgColor(platform)}
              checked={isSelected(platform)}
              onChange={() => handleChange(platform)}
              sx={{ marginRight: theme.spacing(2) }}
              icon={
                <GetSocialMediaIcon
                  socialMedia={platform}
                  sx={{ fontSize: 16 }}
                />
              }
              checkedIcon={
                <GetSocialMediaIcon
                  socialMedia={platform}
                  sx={{ color: 'white', fontSize: 16 }}
                />
              }
            />
          )
      )}
    </Container>
  );
};

export default FilterObjectByPlatform;
